"use client";
import type { StringifiableRecord } from "query-string";
import { get, post, postFormData } from "@/utils/request";
import { useRouter } from "next/navigation";
import useSWR from "swr";
import useSWRMutation from "swr/mutation";
import { qsUrl } from "@/utils/request/share";
import { useAppRouter } from "@/hooks/useRoute";

interface IOptions {
  enabled?: boolean;
  initialData?: any;
  revalidateOnMount?: boolean;
}

const DEFAULT_OPTIONS = {
  revalidateOnFocus: false,
  revalidateOnReconnect: true,
  errorRetryCount: 1,
  errorRetryInterval: 1000,
  dedupingInterval: 2000,
  focusThrottleInterval: 5000,
};

const redirectUrl = (router: any) => {
  if (typeof window !== 'undefined') {
    if (useAppRouter.getState().pathname !== '/login') {
      const pathname = useAppRouter.getState().pathname;
      const searchParams = useAppRouter.getState().searchParams;
      router.push(`/login?redirect=${pathname}?${searchParams?.toString() || ''}`);
    }
  }
};

export const useGet = (
  url: string,
  params?: StringifiableRecord,
  options: IOptions = { enabled: true }
) => {
  const router = useRouter();
  const res = useSWR(
    options.enabled ? qsUrl(url, params || {}) : null,
    async () => get(url, params),
    {
      fallbackData: options.initialData,
      onError: (error) => {
        if (error?.code === 400004 || error?.code === 400001) {
          post("/loginOut").then(() => redirectUrl(router));
        }
      },
      revalidateOnMount: options?.initialData ? false : true,
      ...DEFAULT_OPTIONS,
    }
  );
  return {
    ...res,
    refetch: res.mutate,
  };
};

// 解决水合问题，不能用react-query的post，因为post会改变状态，导致水合问题
export const usePost = (
  url: string,
  params?: any,
  options: IOptions = { enabled: true }
) => {
  const router = useRouter();

  const res = useSWR(
    options.enabled ? qsUrl(url, params) : null,
    async () => {
      return post(url, params);
    },
    {
      fallbackData: options.initialData,
      onError: (error) => {
        if (error?.code === 400004 || error?.code === 400001) {
          post("/loginOut").then(() => redirectUrl(router));
        }
      },
      revalidateOnMount: options?.initialData ? false : true,
      ...DEFAULT_OPTIONS,
    }
  );
  return {
    ...res,
    refetch: res.mutate,
  };
};

export const useMutation = (url: string) => {
  const router = useRouter();
  const res = useSWRMutation(
    url,
    async (key, { arg }: { arg: any }) => {
      // ✅ arg 就是 trigger 时传入的数据
      return post(url, arg);
    },
    {
      onError: (error: any) => {
        if (error?.code === 400004 || error?.code === 400001) {
          post("/loginOut").then(() => router.push("/login"));
        }
      },
      ...DEFAULT_OPTIONS,
    }
  );
  return {
    ...res,
    mutateAsync: res.trigger,
    isPending: res.isMutating,
  };
};

export const useMutationFormData = (url: string) => {
  const router = useRouter();
  const res = useSWRMutation(
    url,
    async (key, { arg }: { arg: any }) => {
      // ✅ arg 就是 trigger 时传入的数据
      const res = await postFormData(url, arg);
      const data = await res.json();
      return data;
    },
    {
      onError: (error: any) => {
        if (error?.code === 400004 || error?.code === 400001) {
          post("/loginOut").then(() => router.push("/login"));
        }
      },
      ...DEFAULT_OPTIONS,
    }
  );
  return {
    ...res,
    mutateAsync: res.trigger,
    isPending: res.isMutating,
  };
};
